import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, CartUpdateEvent } from '@theme/events';

/**
 * State manager for persisting swiper state across page navigation
 */
class SwiperState {
  constructor(sectionId) {
    this.key = `swiper_state_${sectionId}`;
  }

  save(state) {
    try {
      const dataToSave = {
        currentIndex: state.currentIndex,
        history: state.history,
        likedProducts: state.likedProducts,
        currentCollection: state.currentCollection,
        products: state.products, // Save products too for restoration
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.key, JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('State kaydetme hatası:', e);
    }
  }

  load() {
    try {
      const data = sessionStorage.getItem(this.key);
      if (!data) return null;
      
      const state = JSON.parse(data);
      // Expire after 30 minutes
      if (Date.now() - state.timestamp > 30 * 60 * 1000) {
        this.clear();
        return null;
      }
      return state;
    } catch {
      return null;
    }
  }

  clear() {
    try {
      sessionStorage.removeItem(this.key);
    } catch (e) {
      // Ignore
    }
  }
}

/**
 * @typedef {object} ProductSwiperRefs
 * @property {HTMLElement} cardStack
 * @property {HTMLElement} likedStack
 * @property {HTMLElement} rewindBtn
 * @property {HTMLElement} passBtn
 * @property {HTMLElement} likeBtn
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} loadingState
 * @property {HTMLElement} currentCardInfo
 * @property {HTMLElement} progressFill
 * @property {HTMLElement} mobileCounter
 * 
 * @extends Component<ProductSwiperRefs>
 */
export class ProductSwiper extends Component {
  requiredRefs = ['cardStack'];

  /** @type {SwiperState} */
  stateManager;

  /** @type {Array<{id: string, variantId: string, title: string, price: string, comparePrice: string, image: string, url: string}>} */
  products = [];

  /** @type {number} */
  currentIndex = 0;

  /** @type {Array<{productId: string, variantId: string, action: 'like' | 'pass', added?: boolean, index: number}>} */
  history = [];

  /** @type {Array<{id: string, variantId: string, title: string, image: string}>} */
  likedProducts = [];

  /** @type {string} */
  currentCollection = '';

  // Swipe state
  isDragging = false;
  startX = 0;
  startY = 0;
  offsetX = 0;
  offsetY = 0;

  // Pointer gesture direction detection
  touchStartX = 0;
  touchStartY = 0;
  gestureLocked = false;

  /** @type {number | null} */
  activePointerId = null;

  /** @type {number | null} */
  activeTouchId = null;

  /** @type {boolean} */
  useTouchEvents = false;


  // Constants
  SWIPE_THRESHOLD = 80;
  MAX_ROTATION = 12;

  /** @type {HTMLElement | null} */
  activeCard = null;

  /** @type {boolean} */
  isAnimating = false;

  // Progress milestones (read from the section's data attributes in connectedCallback)
  MILESTONE_1 = 3;
  MILESTONE_2 = 10;
  MILESTONE_1_LABEL = '3 Al 2 Öde';
  MILESTONE_2_LABEL = '10 Al 4 Öde';

  /** Milestones already celebrated with a toast; null until first progress update. */
  /** @type {Set<number> | null} */
  #celebratedMilestones = null;

  // Incremented on each collection load to ignore stale fetch responses
  #loadToken = 0;

  connectedCallback() {
    super.connectedCallback();
    this.useTouchEvents = this.#shouldUseTouchEvents();
    
    const sectionId = this.dataset.sectionId || 'default';
    this.stateManager = new SwiperState(sectionId);

    this.#readMilestoneSettings();
    
    this.#initializeProducts();
    this.#bindEvents();
    this.#bindCollectionEvents();
    
    // Save state before page unload
    window.addEventListener('beforeunload', this.#saveState);
    
    // Also save on visibility change (for mobile)
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Save state when clicking links
    this.addEventListener('click', this.#handleLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#saveState();
    this.refs.cardStack?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.refs.cardStack?.removeEventListener('touchstart', this.#handleTouchStart);
    this.#stopPointerTracking();
    this.#stopTouchTracking();
    if (this.isDragging) this.#resetActiveCard();
    this.isDragging = false;
    this.activePointerId = null;
    this.activeTouchId = null;
    window.removeEventListener('beforeunload', this.#saveState);

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.removeEventListener('click', this.#handleLinkClick);
  }

  #readMilestoneSettings() {
    const m1 = parseInt(this.dataset.milestoneOne ?? '', 10);
    const m2 = parseInt(this.dataset.milestoneTwo ?? '', 10);
    if (m1 > 0) this.MILESTONE_1 = m1;
    if (m2 > this.MILESTONE_1) this.MILESTONE_2 = m2;
    else if (this.MILESTONE_2 <= this.MILESTONE_1) this.MILESTONE_2 = this.MILESTONE_1 + 1;
    if (this.dataset.milestoneOneLabel) this.MILESTONE_1_LABEL = this.dataset.milestoneOneLabel;
    if (this.dataset.milestoneTwoLabel) this.MILESTONE_2_LABEL = this.dataset.milestoneTwoLabel;
  }

  #saveState = () => {
    if (this.products.length === 0) return;
    
    this.stateManager.save({
      currentIndex: this.currentIndex,
      history: this.history,
      likedProducts: this.likedProducts,
      currentCollection: this.currentCollection,
      products: this.products,
    });
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') this.#saveState();
  };

  #handleLinkClick = (e) => {
    if (e.target.closest('a')) this.#saveState();
  };

  #localeRoot() {
    const root = window.Theme?.routes?.root_url || '/';
    return root === '/' ? '' : root.replace(/\/+$/, '');
  }

  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[character]);
  }

  #imageUrlWithWidth(imageUrl, width) {
    if (!imageUrl) return '';

    try {
      const url = new URL(imageUrl, window.location.origin);
      url.searchParams.set('width', width.toString());
      return url.toString();
    } catch {
      return imageUrl;
    }
  }

  #imageSrcset(imageUrl) {
    return [320, 480, 640, 800]
      .map((width) => `${this.#imageUrlWithWidth(imageUrl, width)} ${width}w`)
      .join(', ');
  }

  #restoreState() {
    const savedState = this.stateManager.load();
    if (!savedState) return false;
    
    // Only restore if same collection or if we have saved products
    if (savedState.currentCollection && savedState.products && savedState.products.length > 0) {
      this.currentIndex = savedState.currentIndex || 0;
      this.history = savedState.history || [];
      this.likedProducts = savedState.likedProducts || [];
      this.currentCollection = savedState.currentCollection;
      this.products = savedState.products;
      return true;
    }
    
    return false;
  }

  #initializeProducts() {
    // First try to restore state
    const restored = this.#restoreState();
    
    if (restored) {
      // State was restored, render from saved state
      this.#renderCards();
      this.#updateLikedStack();
      this.#updateUI();
      
      // Update active collection pill
      const section = this.closest('.product-swiper-section');
      section?.querySelectorAll('.collection-pill').forEach(pill => {
        const isActive = pill.dataset.collection === this.currentCollection;
        pill.classList.toggle('active', isActive);
        pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      return;
    }
    
    // No saved state, initialize from data attributes
    const productsData = this.dataset.products;
    const collectionHandle = this.dataset.collection || '';
    
    if (productsData) {
      try {
        this.products = JSON.parse(productsData);
        this.currentCollection = collectionHandle;
        this.#renderCards();
        this.#updateUI();
      } catch (e) {
        console.error('Ürün verileri ayrıştırma hatası:', e);
      }
    }
  }

  #bindEvents() {
    if (this.useTouchEvents) {
      this.refs.cardStack?.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    } else {
      this.refs.cardStack?.addEventListener('pointerdown', this.#handlePointerDown);
    }
  }

  #shouldUseTouchEvents() {
    const touchCapable = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    const isAppleWebKit = /AppleWebKit/i.test(navigator.userAgent);
    const isIOS =
      /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    return touchCapable && isAppleWebKit && isIOS;
  }

  #startPointerTracking() {
    window.addEventListener('pointermove', this.#handlePointerMove, { passive: false });
    window.addEventListener('pointerup', this.#handlePointerUp, { passive: true });
    window.addEventListener('pointercancel', this.#handlePointerCancel, { passive: true });
    window.addEventListener('blur', this.#handleWindowBlur);
  }

  #stopPointerTracking() {
    window.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('pointerup', this.#handlePointerUp);
    window.removeEventListener('pointercancel', this.#handlePointerCancel);
    window.removeEventListener('blur', this.#handleWindowBlur);
  }

  #startTouchTracking() {
    window.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    window.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.#handleTouchCancel, { passive: true });
    window.addEventListener('blur', this.#handleWindowBlur);
  }

  #stopTouchTracking() {
    window.removeEventListener('touchmove', this.#handleTouchMove);
    window.removeEventListener('touchend', this.#handleTouchEnd);
    window.removeEventListener('touchcancel', this.#handleTouchCancel);
    window.removeEventListener('blur', this.#handleWindowBlur);
  }

  #findTouch(touchList) {
    if (this.activeTouchId === null) return null;
    for (let index = 0; index < touchList.length; index++) {
      if (touchList[index].identifier === this.activeTouchId) return touchList[index];
    }
    return null;
  }


  #bindCollectionEvents() {
    const section = this.closest('.product-swiper-section');
    const collectionNav = section?.querySelector('.collection-nav');
    
    if (collectionNav) {
      collectionNav.addEventListener('click', this.#handleCollectionClick);
    }
  }

  #handleCollectionClick = (e) => {
    const button = e.target.closest('.collection-pill');
    if (!button) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const collectionHandle = button.dataset.collection;
    if (!collectionHandle || collectionHandle === this.currentCollection) return;

    const section = this.closest('.product-swiper-section');
    section?.querySelectorAll('.collection-pill').forEach(pill => {
      pill.classList.remove('active');
      pill.setAttribute('aria-selected', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');

    this.#loadCollection(collectionHandle);
  };

  #handlePointerDown = (e) => {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if (e.target.closest('a, button')) return;

    if (this.isDragging) this.#cancelPointerDrag();

    this.activePointerId = e.pointerId;
    this.touchStartX = e.clientX;
    this.touchStartY = e.clientY;
    this.gestureLocked = e.pointerType === 'mouse';
    this.#startDrag(e.clientX, e.clientY);
    if (!this.isDragging) {
      this.activePointerId = null;
      return;
    }
    this.#startPointerTracking();

    if (e.pointerType === 'mouse' && e.cancelable) e.preventDefault();
  };

  #handleTouchStart = (e) => {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    if (e.touches.length !== 1) return;
    if (e.target instanceof Element && e.target.closest('a, button')) return;

    if (this.isDragging) this.#cancelPointerDrag();

    const touch = e.changedTouches[0] || e.touches[0];
    if (!touch) return;

    this.activeTouchId = touch.identifier;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.gestureLocked = false;
    this.#startDrag(touch.clientX, touch.clientY);
    if (!this.isDragging) {
      this.activeTouchId = null;
      return;
    }
    this.#startTouchTracking();
  };

  #handleTouchMove = (e) => {
    if (!this.isDragging) return;
    const touch = this.#findTouch(e.touches);
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (!this.gestureLocked) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.#cancelPointerDrag();
        return;
      }
      this.gestureLocked = true;
    }

    if (e.cancelable) e.preventDefault();
    this.#moveDrag(touch.clientX, touch.clientY);
  };

  #handleTouchEnd = (e) => {
    if (!this.isDragging || !this.#findTouch(e.changedTouches)) return;
    this.activeTouchId = null;
    this.#stopTouchTracking();
    this.#endDrag();
  };

  #handleTouchCancel = (e) => {
    if (this.activeTouchId !== null && e.changedTouches.length > 0 && !this.#findTouch(e.changedTouches)) return;
    this.#cancelPointerDrag();
  };

  #handlePointerMove = (e) => {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;

    const deltaX = e.clientX - this.touchStartX;
    const deltaY = e.clientY - this.touchStartY;

    if (e.pointerType !== 'mouse' && !this.gestureLocked) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.#cancelPointerDrag();
        return;
      }
      this.gestureLocked = true;
    }

    if (e.cancelable) e.preventDefault();
    this.#moveDrag(e.clientX, e.clientY);
  };

  #resetActiveCard() {
    if (!this.activeCard) return;
    this.activeCard.classList.remove('dragging');
    this.activeCard.style.transition = 'transform 0.2s ease';
    this.activeCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
    const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
    const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
    if (likeIndicator) likeIndicator.style.opacity = '0';
    if (passIndicator) passIndicator.style.opacity = '0';
  }


  #handlePointerUp = (e) => {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;
    this.activePointerId = null;
    this.#stopPointerTracking();
    this.#endDrag();
  };

  #handlePointerCancel = (e) => {
    if (e.pointerId !== this.activePointerId) return;
    this.#cancelPointerDrag();
  };

  #handleWindowBlur = () => {
    if (this.isDragging) this.#cancelPointerDrag();
  };

  #cancelPointerDrag() {
    this.#resetActiveCard();
    this.isDragging = false;
    this.activePointerId = null;
    this.activeTouchId = null;
    this.#stopPointerTracking();
    this.#stopTouchTracking();
  }

  #startDrag(x, y) {
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    if (!this.activeCard) return;

    this.isDragging = true;
    this.startX = x;
    this.startY = y;
    this.offsetX = 0;
    this.offsetY = 0;

    this.activeCard.classList.add('dragging');
  }

  #moveDrag(x, y) {
    if (!this.activeCard) return;

    this.offsetX = x - this.startX;
    this.offsetY = (y - this.startY) * 0.4;

    const rotation = (this.offsetX / window.innerWidth) * this.MAX_ROTATION;
    
    this.activeCard.style.transform = `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) rotate(${rotation}deg)`;
    
    const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
    const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
    
    if (likeIndicator && passIndicator) {
      const progress = Math.min(Math.abs(this.offsetX) / this.SWIPE_THRESHOLD, 1);
      if (this.offsetX > 20) {
        likeIndicator.style.opacity = progress.toString();
        passIndicator.style.opacity = '0';
      } else if (this.offsetX < -20) {
        passIndicator.style.opacity = progress.toString();
        likeIndicator.style.opacity = '0';
      } else {
        likeIndicator.style.opacity = '0';
        passIndicator.style.opacity = '0';
      }
    }
  }

  #endDrag() {
    if (!this.activeCard) return;

    this.isDragging = false;
    this.activeCard.classList.remove('dragging');

    const shouldSwipeRight = this.offsetX > this.SWIPE_THRESHOLD;
    const shouldSwipeLeft = this.offsetX < -this.SWIPE_THRESHOLD;

    if (shouldSwipeRight) {
      this.#swipeCard('right');
    } else if (shouldSwipeLeft) {
      this.#swipeCard('left');
    } else {
      this.activeCard.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
      this.activeCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      
      const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
      const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
      if (likeIndicator) likeIndicator.style.opacity = '0';
      if (passIndicator) passIndicator.style.opacity = '0';
      
      setTimeout(() => {
        if (this.activeCard) {
          this.activeCard.style.transition = '';
        }
      }, 400);
    }
  }

  #swipeCard(direction) {
    if (!this.activeCard || this.isAnimating) return;
    
    this.isAnimating = true;
    const card = this.activeCard;
    
    const exitX = direction === 'right' ? window.innerWidth : -window.innerWidth;
    const exitRotation = direction === 'right' ? 20 : -20;

    const indicator = card.querySelector(direction === 'right' ? '.swipe-indicator--like' : '.swipe-indicator--pass');
    if (indicator) indicator.style.opacity = '1';

    card.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease';
    card.style.transform = `translate3d(${exitX}px, ${this.offsetY}px, 0) rotate(${exitRotation}deg)`;
    card.style.opacity = '0';
    card.style.pointerEvents = 'none';

    setTimeout(() => {
      if (direction === 'right') {
        this.#processLike();
      } else {
        this.#processPass();
      }
      
      card.remove();
      this.currentIndex++;
      this.#activateNextCard();
      this.#updateUI();
      this.isAnimating = false;
      this.#saveState();
    }, 500);
  }

  #activateNextCard() {
    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (!cards || cards.length === 0) {
      this.#showEmptyState();
      return;
    }

    cards.forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      if (index === 0) {
        card.classList.add('active');
        card.style.transform = '';
        card.style.opacity = '';
        card.style.transition = '';
      } else {
        card.classList.remove('active');
      }
    });

    const nextProductIndex = this.currentIndex + cards.length;
    if (nextProductIndex < this.products.length) {
      const product = this.products[nextProductIndex];
      const newCard = this.#createCard(product, cards.length);
      this.refs.cardStack?.appendChild(newCard);
    }
  }

  #processLike() {
    const product = this.products[this.currentIndex];
    if (!product) return;

    const alreadyLiked = this.likedProducts.some(p => p.variantId === product.variantId);

    const historyEntry = {
      productId: product.id, 
      variantId: product.variantId, 
      action: 'like',
      added: !alreadyLiked,
      index: this.currentIndex 
    };
    this.history.push(historyEntry);

    // Already liked this variant earlier (e.g. revisiting a collection or the
    // same product appearing in multiple collections): keep the swipe, but
    // don't add a duplicate to the cart or double-count progress.
    if (alreadyLiked) return;

    this.likedProducts.push({
      id: product.id,
      variantId: product.variantId,
      title: product.title,
      image: product.image,
    });
    
    this.#updateLikedStack();
    this.#addToCart(product.variantId).then((success) => {
      if (success) {
        this.#checkMilestone();
        this.#saveState();
        return;
      }

      historyEntry.added = false;
      this.likedProducts = this.likedProducts.filter((likedProduct) => likedProduct.variantId !== product.variantId);
      this.#updateLikedStack();
      this.#updateUI();
      this.#saveState();
      this.#showCartError();
    });
  }

  #processPass() {
    const product = this.products[this.currentIndex];
    if (!product) return;

    this.history.push({ 
      productId: product.id, 
      variantId: product.variantId, 
      action: 'pass',
      index: this.currentIndex 
    });
  }

  #checkMilestone() {
    const count = this.likedProducts.length;
    const celebrated = this.#getCelebratedMilestones();

    // Fire on crossing a threshold (not only on an exact count), so quick
    // double-likes or out-of-order cart responses can't skip the toast,
    // and each milestone is celebrated only once until it's lost again.
    let newlyReached = 0;
    [this.MILESTONE_1, this.MILESTONE_2].forEach((threshold, i) => {
      if (count >= threshold && !celebrated.has(threshold)) {
        celebrated.add(threshold);
        newlyReached = i + 1;
      }
    });

    if (newlyReached) this.#showMilestoneReached(newlyReached);
  }

  #getCelebratedMilestones() {
    if (!this.#celebratedMilestones) {
      // First run (fresh load or restored session): treat milestones already
      // reached as celebrated so a page reload doesn't replay the toast.
      const count = this.likedProducts.length;
      this.#celebratedMilestones = new Set(
        [this.MILESTONE_1, this.MILESTONE_2].filter((threshold) => count >= threshold)
      );
    }
    return this.#celebratedMilestones;
  }

  #showMilestoneReached(milestone) {
    const toast = document.createElement('div');
    toast.className = 'milestone-toast';
    const label = milestone === 1 ? this.MILESTONE_1_LABEL : this.MILESTONE_2_LABEL;
    toast.innerHTML = `<span class="milestone-toast__icon">${milestone === 1 ? '🎉' : '🎁'}</span><span>${this.#escape(label)} kazandın!</span>`;
    
    this.appendChild(toast);
    
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  #showCartError() {
    const toast = document.createElement('div');
    toast.className = 'milestone-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
    this.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  async #fetchCart() {
    const response = await fetch(`${this.#localeRoot()}/cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Sepet bilgisi alınamadı.');
    return response.json();
  }

  async #addToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        headers: { Accept: 'application/json' },
      });

      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Ürün sepete eklenemedi.');

      const cart = await this.#fetchCart();
      this.dispatchEvent(
        new CartAddEvent(cart, 'product-swiper', {
          source: 'product-swiper',
          itemCount: cart.item_count,
          variantId,
        })
      );
      return true;
    } catch (error) {
      console.error('Sepete eklenirken hata:', error);
      this.dispatchEvent(
        new CartErrorEvent('product-swiper', error.message || 'Ürün sepete eklenemedi.', error.message || '', null)
      );
      return false;
    }
  }

  handleLike() {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('right');
  }

  handlePass() {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('left');
  }

  handleRewind() {
    if (this.history.length === 0 || this.isAnimating) return;

    const lastAction = this.history.pop();
    if (!lastAction) return;

    this.isAnimating = true;
    this.currentIndex--;

    if (lastAction.action === 'like' && lastAction.added) {
      this.#removeFromCart(lastAction.variantId);
      this.likedProducts = this.likedProducts.filter(p => p.variantId !== lastAction.variantId);
      this.#updateLikedStack();
    }

    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (cards && cards.length > 0) {
      cards[cards.length - 1]?.remove();
    }

    const product = this.products[this.currentIndex];
    const newCard = this.#createCard(product, 0);
    newCard.classList.add('active');
    
    const startX = lastAction.action === 'like' ? window.innerWidth : -window.innerWidth;
    newCard.style.transform = `translate3d(${startX}px, 0, 0) rotate(${lastAction.action === 'like' ? 20 : -20}deg)`;
    newCard.style.opacity = '0';
    
    this.refs.cardStack?.prepend(newCard);

    const allCards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    allCards?.forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      if (index === 0) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });

    requestAnimationFrame(() => {
      newCard.style.transition = 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease';
      newCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      newCard.style.opacity = '1';
    });

    setTimeout(() => {
      newCard.style.transition = '';
      this.isAnimating = false;
      this.#saveState();
    }, 500);

    this.#hideEmptyState();
    this.#updateUI();
  }

  async #removeFromCart(variantId) {
    try {
      const root = this.#localeRoot();

      // Decrement by one rather than removing the whole line, so we don't wipe
      // units the customer may have added elsewhere.
      const cartResponse = await fetch(`${root}/cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Sepet bilgisi alınamadı.');
      const cart = await cartResponse.json();
      const line = cart.items?.find(item => item.variant_id?.toString() === variantId.toString());
      const newQuantity = line ? Math.max(0, line.quantity - 1) : 0;

      const changeUrl = window.Theme?.routes?.cart_change_url || `${root}/cart/change`;
      const changeResponse = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: newQuantity }),
      });
      const updatedCart = await changeResponse.json();
      if (!changeResponse.ok || updatedCart.status) {
        throw new Error(updatedCart.description || updatedCart.message || 'Sepet güncellenemedi.');
      }

      this.dispatchEvent(
        new CartUpdateEvent(updatedCart, 'product-swiper', {
          source: 'product-swiper',
          itemCount: updatedCart.item_count,
          variantId,
        })
      );
    } catch (error) {
      console.error('Sepetten çıkarılırken hata:', error);
      this.dispatchEvent(
        new CartErrorEvent('product-swiper', error.message || 'Sepet güncellenemedi.', error.message || '', null)
      );
    }
  }

  async #loadCollection(collectionHandle) {
    this.#showLoadingState();

    // Guard against rapid pill taps: only the latest request may apply.
    const requestToken = ++this.#loadToken;
    const root = this.#localeRoot();
    const limit = Math.min(Number(this.dataset.maxProducts) || 50, 50);

    try {
      const response = await fetch(`${root}/collections/${collectionHandle}/products.json?limit=${limit}`);
      const data = await response.json();

      if (requestToken !== this.#loadToken) return;

      this.products = data.products.map(product => {
        const variant = product.variants.find(v => v.available) || product.variants[0] || {};
        const rawImage = product.images[0]?.src || '';
        return {
          id: product.id.toString(),
          variantId: variant.id ? variant.id.toString() : '',
          title: product.title,
          // Shopify products.json returns prices as strings like "405.90", not cents
          price: this.#formatMoney(variant.price),
          comparePrice: variant.compare_at_price ? this.#formatMoney(variant.compare_at_price) : '',
          image: rawImage ? `${rawImage}${rawImage.includes('?') ? '&' : '?'}width=800` : '',
          url: `${root}/products/${product.handle}`,
        };
      });

      // Reset only the per-collection deck. Keep likedProducts so the progress
      // bar, milestones, liked stack and counter persist across collections.
      this.currentIndex = 0;
      this.history = [];
      this.currentCollection = collectionHandle;
      
      this.#renderCards();
      this.#updateLikedStack();
      this.#hideLoadingState();
      this.#updateUI();
      this.#saveState();
    } catch (error) {
      if (requestToken !== this.#loadToken) return;
      console.error('Koleksiyon yüklenirken hata:', error);
      this.#hideLoadingState();
    }
  }

  #formatMoney(price) {
    // Shopify products.json returns price as string like "405.90" or number
    // Parse it and format for Turkish locale
    let amount;
    if (typeof price === 'string') {
      // Remove any existing currency symbols and parse
      amount = parseFloat(price.replace(/[^0-9.,]/g, '').replace(',', '.'));
    } else {
      amount = parseFloat(price) || 0;
    }
    
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  #renderCards() {
    const cardStack = this.refs.cardStack;
    if (!cardStack) return;

    cardStack.replaceChildren();

    const visibleProducts = this.products.slice(this.currentIndex, this.currentIndex + 3);
    
    if (visibleProducts.length === 0) {
      this.#showEmptyState();
      return;
    }

    this.#hideEmptyState();

    const fragment = document.createDocumentFragment();
    visibleProducts.forEach((product, index) => {
      const card = this.#createCard(product, index);
      if (index === 0) {
        card.classList.add('active');
      }
      fragment.appendChild(card);
    });
    cardStack.replaceChildren(fragment);
  }

  #createCard(product, stackIndex) {
    const card = document.createElement('div');
    card.className = 'swipe-card';
    card.dataset.productId = product.id;
    card.style.setProperty('--stack-index', stackIndex.toString());
    
    const hasComparePrice = product.comparePrice && product.comparePrice !== product.price;
    const title = this.#escape(product.title);
    const price = this.#escape(product.price);
    const comparePrice = this.#escape(product.comparePrice);
    const imageUrl = this.#escape(this.#imageUrlWithWidth(product.image, 800));
    const imageSrcset = this.#escape(this.#imageSrcset(product.image));
    const productUrl = this.#escape(product.url);

    card.innerHTML = `
      <div class="swipe-card__inner">
        <div class="swipe-card__image-wrapper">
          ${product.image 
            ? `<img src="${imageUrl}" srcset="${imageSrcset}" sizes="(max-width: 749px) calc(100vw - 48px), 420px" alt="${title}" class="swipe-card__image" loading="${stackIndex === 0 ? 'eager' : 'lazy'}" ${stackIndex === 0 ? 'fetchpriority="high"' : ''} decoding="async" draggable="false" width="600" height="800" />`

            : `<div class="swipe-card__placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>`
          }
          <div class="swipe-indicator swipe-indicator--like">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <span>SEPETE EKLE</span>
          </div>
          <div class="swipe-indicator swipe-indicator--pass">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            <span>GEÇ</span>
          </div>
        </div>
        <div class="swipe-card__content">
          <h3 class="swipe-card__title">${title}</h3>
          <div class="swipe-card__price-row">
            <span class="swipe-card__price">${price}</span>
            ${hasComparePrice ? `<span class="swipe-card__compare-price">${comparePrice}</span>` : ''}
          </div>
          <a href="${productUrl}" class="swipe-card__link">
            <span>Ürünü İncele</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </div>
    `;

    return card;
  }

  #updateLikedStack() {
    const likedStack = this.refs.likedStack;
    if (!likedStack) return;

    if (this.likedProducts.length === 0) {
      likedStack.innerHTML = `
        <div class="liked-stack__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>Beğendiğin ürünler burada görünecek</span>
        </div>
      `;
      return;
    }

    likedStack.innerHTML = '';
    
    const displayProducts = this.likedProducts.slice(-4);
    
    displayProducts.forEach((product, index) => {
      const item = document.createElement('div');
      item.className = 'liked-stack__item';
      item.style.setProperty('--stack-offset', (displayProducts.length - 1 - index).toString());
      const image = document.createElement('img');
      image.src = this.#imageUrlWithWidth(product.image, 160);
      image.alt = product.title || '';
      image.draggable = false;
      image.loading = 'lazy';
      item.appendChild(image);
      likedStack.appendChild(item);
    });

    const counter = document.createElement('div');
    counter.className = 'liked-stack__counter';
    const counterText = document.createElement('span');
    counterText.textContent = this.likedProducts.length.toString();
    counter.appendChild(counterText);
    likedStack.appendChild(counter);
  }

  #updateUI() {
    this.#updateRewindButton();
    this.#updateCounter();
    this.#updateProgressBar();
    this.#updateMobileCounter();
  }

  #updateRewindButton() {
    const rewindBtn = this.refs.rewindBtn;
    if (rewindBtn) {
      const canRewind = this.history.length > 0;
      rewindBtn.disabled = !canRewind;
      rewindBtn.classList.toggle('disabled', !canRewind);
    }
  }

  #updateCounter() {
    const counter = this.refs.currentCardInfo;
    if (counter) {
      const remaining = Math.max(0, this.products.length - this.currentIndex);
      counter.textContent = `${remaining} ürün kaldı`;
    }
  }

  #updateProgressBar() {
    // Find progress elements in the section (they're outside this component)
    const section = this.closest('.product-swiper-section');
    if (!section) return;
    
    const progressFill = section.querySelector('.progress-bar__fill');
    const milestone1El = section.querySelector('.progress-milestone[data-milestone="1"]');
    const milestone2El = section.querySelector('.progress-milestone[data-milestone="2"]');
    const progressText = section.querySelector('.progress-text');
    const label1 = section.querySelector('.progress-label[data-for="1"]');
    const label2 = section.querySelector('.progress-label[data-for="2"]');
    
    const count = this.likedProducts.length;
    const maxMilestone = this.MILESTONE_2;

    // Forget celebrations for milestones lost via rewind, so re-reaching them toasts again
    const celebrated = this.#getCelebratedMilestones();
    celebrated.forEach((threshold) => {
      if (count < threshold) celebrated.delete(threshold);
    });
    const progress = Math.min((count / maxMilestone) * 100, 100);
    
    // Update progress bar fill
    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
    
    // Update milestone 1 state
    if (milestone1El) {
      const isReached = count >= this.MILESTONE_1;
      const isActive = count > 0 && count < this.MILESTONE_1;
      milestone1El.classList.toggle('reached', isReached);
      milestone1El.classList.toggle('active', isActive);
    }
    
    // Update milestone 2 state
    if (milestone2El) {
      const isReached = count >= this.MILESTONE_2;
      const isActive = count >= this.MILESTONE_1 && count < this.MILESTONE_2;
      milestone2El.classList.toggle('reached', isReached);
      milestone2El.classList.toggle('active', isActive);
    }
    
    // Update label colors
    if (label1) {
      label1.classList.toggle('reached', count >= this.MILESTONE_1);
    }
    if (label2) {
      label2.classList.toggle('reached', count >= this.MILESTONE_2);
    }
    
    // Update progress text
    if (progressText) {
      if (count >= this.MILESTONE_2) {
        progressText.textContent = 'Tüm ödülleri kazandın! 🎉';
      } else if (count >= this.MILESTONE_1) {
        progressText.textContent = `${this.MILESTONE_2_LABEL} için ${this.MILESTONE_2 - count} ürün daha ekle!`;
      } else {
        progressText.textContent = `${this.MILESTONE_1_LABEL} için ${this.MILESTONE_1 - count} ürün daha ekle!`;
      }
    }
  }

  #updateMobileCounter() {
    const mobileCounter = this.refs.mobileCounter;
    if (mobileCounter) {
      const count = this.likedProducts.length;
      mobileCounter.textContent = count.toString();
      mobileCounter.classList.toggle('has-items', count > 0);
    }
  }

  #showEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = false;
  }

  #hideEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = true;
  }

  #showLoadingState() {
    const loadingState = this.refs.loadingState;
    if (loadingState) loadingState.hidden = false;
    this.refs.cardStack?.classList.add('loading');
  }

  #hideLoadingState() {
    const loadingState = this.refs.loadingState;
    if (loadingState) loadingState.hidden = true;
    this.refs.cardStack?.classList.remove('loading');
  }
}

if (!customElements.get('product-swiper')) {
  customElements.define('product-swiper', ProductSwiper);
}
